import { RawSkillAssessmentDto, SkillAssessmentDto, SkillDto, SkillTypeDto } from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import Loader from '@/components/Loader.tsx';
import { useAssessUserSkills } from '@/hooks/useAssessUserSkills.ts';
import { useGetSkillsAssessment } from '@/hooks/useGetSkillsAssessment.ts';
import { useGetUserSkillsAssessment } from '@/hooks/useGetUserSkillsAssessment.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SkillSection from './SkillSection';
import SkillsIntroText from './SkillsIntroText';
import { getOrderedSkillGroups, groupSkillsByType } from './utils/groupSkillsByType.util';
import { getScaleForSkillType } from './utils/ratingScales';

const MAX_LEVEL = 4;

const UserSkillsAssessment: React.FC = () => {
  const { t: tButtons } = useTranslationWithPrefix('quizInitializedPage.buttons');
  const { t } = useTranslation();

  const { isGettingSkills, skills } = useGetSkillsAssessment();
  const { isGetting, userSkills } = useGetUserSkillsAssessment(!!skills?.length);
  const { assessUserSkills, isLoading } = useAssessUserSkills();

  const [skillAssessment, setSkillAssessment] = useState<RawSkillAssessmentDto[]>([]);

  const handleChange =
    (skillName: string, skillId: number) => (_event: Event, value: number[] | number) => {
      if (typeof value !== 'number') {
        return;
      }

      setSkillAssessment(state => {
        const skill = state.find(item => item.skillId === skillId);
        const newSkillAssessment = { level: value, skillId, skillName };

        if (!skill) {
          return [...state, newSkillAssessment];
        }
        return state.map(item => (item.skillId === skillId ? newSkillAssessment : item));
      });
    };

  const handleGoNext = async () => {
    await assessUserSkills(skillAssessment);
  };

  useEffect(() => {
    const updateSkillAssessment = (newSkills: SkillAssessmentDto[]) => {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSkillAssessment(state =>
        state.map(stateItem => ({
          ...stateItem,
          level: Math.min(
            newSkills.find(item => item.skill.skillId === stateItem.skillId)?.level ??
              stateItem.level,
            MAX_LEVEL,
          ),
        })),
      );
    };
    if (userSkills?.length) {
      updateSkillAssessment(userSkills);
    }
  }, [userSkills]);

  useEffect(() => {
    const updateSkillAssessment = (newSkills: SkillDto[]) => {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSkillAssessment(
        newSkills.map(item => ({
          level: item.type === SkillTypeDto.Language ? 0 : 1,
          skillId: item.skillId,
          skillName: item.name,
        })),
      );
    };
    if (skills?.length) {
      updateSkillAssessment(skills);
    }
  }, [skills]);

  if (isGetting || isGettingSkills) {
    return <Loader className="min-h-dvh" />;
  }
  if (!skills) {
    return null;
  }

  const groupedSkills = groupSkillsByType(skills);
  const orderedGroups = getOrderedSkillGroups(groupedSkills);

  return (
    <div className="w-full max-w-4xl mx-auto px-0 md:px-4">
      <SkillsIntroText />

      <div className="mb-6 w-full">
        {orderedGroups.map(([type, typeSkills]) => (
          <SkillSection
            disabled={isLoading}
            key={type}
            onChange={handleChange}
            scale={getScaleForSkillType(type, t)}
            skillAssessment={skillAssessment}
            skills={typeSkills}
            type={type}
          />
        ))}
      </div>

      <div className="pt-4">
        <ButtonWithLoading
          className="w-full sm:w-1/3"
          disabled={!skillAssessment.length}
          isLoading={isLoading}
          onClick={handleGoNext}
        >
          {tButtons('next')}
        </ButtonWithLoading>
      </div>
    </div>
  );
};

export default UserSkillsAssessment;
